import type { NavigatorSignals, PluginSignal, UaDataSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, attemptAsync } from '../util/safe.js';
import { parseUserAgent } from '../util/ua.js';

interface NavigatorUaData {
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
}

const HIGH_ENTROPY_HINTS = [
  'architecture',
  'bitness',
  'model',
  'platformVersion',
  'uaFullVersion',
  'fullVersionList',
  'wow64',
];

async function collectUaData(): Promise<UaDataSignals | null> {
  const uaData = attempt(
    () => (navigator as Navigator & { userAgentData?: NavigatorUaData }).userAgentData,
  );
  if (!uaData) return null;

  // Низкоэнтропийная часть доступна синхронно и без разрешений.
  const base: UaDataSignals = {
    brands: attempt(() => uaData.brands?.map((b) => ({ brand: b.brand, version: b.version })) ?? null),
    fullVersionList: null,
    mobile: attempt(() => uaData.mobile ?? null),
    platform: attempt(() => uaData.platform ?? null),
    platformVersion: null,
    architecture: null,
    bitness: null,
    model: null,
    uaFullVersion: null,
    wow64: null,
  };

  const high = await attemptAsync(() => uaData.getHighEntropyValues?.(HIGH_ENTROPY_HINTS));
  if (!high) return base;

  const list = high['fullVersionList'];
  return {
    ...base,
    fullVersionList: Array.isArray(list)
      ? list.map((b: { brand?: string; version?: string }) => ({
          brand: String(b.brand ?? ''),
          version: String(b.version ?? ''),
        }))
      : null,
    platformVersion: str(high['platformVersion']),
    architecture: str(high['architecture']),
    bitness: str(high['bitness']),
    model: str(high['model']),
    uaFullVersion: str(high['uaFullVersion']),
    wow64: typeof high['wow64'] === 'boolean' ? high['wow64'] : null,
  };
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function collectPlugins(): PluginSignal[] | null {
  return attempt(() => {
    const plugins = navigator.plugins;
    if (!plugins) return null;
    const out: PluginSignal[] = [];
    for (let i = 0; i < plugins.length; i++) {
      const p = plugins[i];
      if (!p) continue;
      const mimeTypes: PluginSignal['mimeTypes'] = [];
      for (let j = 0; j < p.length; j++) {
        const mt = p[j];
        if (mt) {
          mimeTypes.push({
            type: mt.type,
            suffixes: mt.suffixes,
            description: mt.description,
          });
        }
      }
      out.push({
        name: p.name,
        description: p.description,
        filename: p.filename,
        mimeTypes,
      });
    }
    return out;
  });
}

export const navigatorModule = defineModule({
  id: 'navigator',
  phase: 0,
  timeoutMs: 2000,
  async run(): Promise<NavigatorSignals> {
    const nav = navigator as Navigator & Record<string, unknown>;
    const userAgent = attempt(() => nav.userAgent);

    return {
      userAgent,
      appVersion: attempt(() => nav.appVersion),
      appName: attempt(() => nav.appName),
      appCodeName: attempt(() => nav.appCodeName),
      platform: attempt(() => nav.platform),
      vendor: attempt(() => nav.vendor),
      vendorSub: attempt(() => nav['vendorSub'] as string),
      product: attempt(() => nav.product),
      productSub: attempt(() => nav['productSub'] as string),
      // oscpu и buildID есть только в Gecko — их наличие само по себе сигнал.
      oscpu: attempt(() => nav['oscpu'] as string),
      buildID: attempt(() => nav['buildID'] as string),
      language: attempt(() => nav.language),
      languages: attempt(() => (nav.languages ? Array.from(nav.languages) : null)),
      hardwareConcurrency: attempt(() => nav.hardwareConcurrency),
      deviceMemory: attempt(() => nav['deviceMemory'] as number),
      maxTouchPoints: attempt(() => nav.maxTouchPoints),
      webdriver: attempt(() => nav.webdriver),
      pdfViewerEnabled: attempt(() => nav.pdfViewerEnabled),
      cookieEnabled: attempt(() => nav.cookieEnabled),
      doNotTrack: attempt(() => nav.doNotTrack),
      globalPrivacyControl: attempt(() => nav['globalPrivacyControl'] as boolean),
      onLine: attempt(() => nav.onLine),
      javaEnabled: attempt(() => nav.javaEnabled()),
      plugins: collectPlugins(),
      mimeTypesCount: attempt(() => navigator.mimeTypes?.length ?? null),
      uaData: await collectUaData(),
      // Порядок ключей задаётся движком; переопределённые свойства всплывают
      // как собственные ключи экземпляра вместо прототипных.
      ownKeys: attempt(() => Object.getOwnPropertyNames(nav)),
      prototypeKeys: attempt(() => Object.getOwnPropertyNames(Object.getPrototypeOf(nav))),
      connection: attempt(() => {
        const c = nav['connection'] as Record<string, unknown> | undefined;
        if (!c) return null;
        return {
          effectiveType: str(c['effectiveType']),
          rtt: typeof c['rtt'] === 'number' ? c['rtt'] : null,
          downlink: typeof c['downlink'] === 'number' ? c['downlink'] : null,
          saveData: typeof c['saveData'] === 'boolean' ? c['saveData'] : null,
          type: str(c['type']),
        };
      }),
      parsed: parseUserAgent(userAgent),
    };
  },
});
